import { getDeliveryByEventLabel, getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { BaseFieldObject } from '@noths/polaris-dev-ts-types';

import type { Product } from 'src/redux/products/types/Product';
import type { PageTrackingContext } from 'src/tracking/utils/getPageTrackingContext';
import type { ImpressionFieldObject, ProductFieldObject } from 'src/types/googleAnalytics';
import { PageType } from 'src/types/navigation';
import { logger } from './serverLogger';

const generateFieldObject = (
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
): BaseFieldObject => {
  if (!product) {
    logger.warn('Product is required for generateFieldObject');
    return {} as BaseFieldObject;
  }

  const {
    code,
    delivery,
    deliveryByEvent,
    isNew,
    onSale,
    partner,
    price,
    purchasable,
    salePercentage,
    title,
  } = product;
  const findProductByCode = (prod: Product) => prod.code === product.code;
  const productsArr = Object.values(items).flat();
  const pageProductPosition =
    (productsArr.findIndex(findProductByCode) % productsReturnedCount) + 1;

  const findProductWithinPage = (pageNumber: string) =>
    items[Number(pageNumber)].find(findProductByCode);
  const pages = Object.keys(items);
  const pageNumber = Number(pages.find(findProductWithinPage));

  /*
    Use page product position if on the 1st page.
    Otherwise, count number of products spanning all previous pages,
    and add page product position to said number.
  */
  const dimension25 =
    pageNumber === 1
      ? pageProductPosition
      : pageProductPosition + (pageNumber - 1) * productsReturnedCount;

  const dimension22List: string[] = [
    /* Zero, not merely known: a quoted option always has a cost, so a presence
       check would tag every product we can quote at all, paid delivery included. */
    delivery?.cost === 0 && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
    deliveryByEvent?.arriveByEvent && getDeliveryByEventLabel(deliveryByEvent.eventName),
  ].filter(Boolean) as string[];

  return {
    brand: partner.name,
    dimension22: dimension22List.join(','),
    dimension25: dimension25,
    dimension30: String(code),
    dimension31: String(price.amount / 100),
    dimension32: String(price.amount / 100),
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    dimension34: partner.shortcode,
    id: String(getProductIdFromProductCode(code)),
    name: title,
    position: pageProductPosition,
    price: String(price.amount / 100),
  };
};

const getDimension16PartialFieldObject = (
  product: Product | undefined,
  context: PageTrackingContext,
) => {
  if (!product) {
    logger.warn('Product is required for getDimension16PartialFieldObject');
    return {} as Partial<BaseFieldObject>;
  }

  const { pageIdentifier, pageType } = context;

  if (!pageType) {
    return {};
  }

  // Partner page uses the lowercase partner name for dimension16.
  // All other page types use their generic page identifier.
  if (pageType === PageType.Partner) {
    return { dimension16: product.partner?.name?.toLowerCase() };
  }

  if (pageIdentifier !== undefined) {
    return { dimension16: String(pageIdentifier) };
  }

  return {};
};

export const getProductFieldObject = <T extends PageType>(
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
  context: PageTrackingContext,
): ProductFieldObject<T> => {
  if (!product) {
    logger.warn('Product is required for getProductFieldObject');
    return {} as ProductFieldObject<T>;
  }

  const fieldObject = generateFieldObject(product, items, productsReturnedCount);

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    product,
    context,
  ) as ProductFieldObject<T>;

  return {
    ...fieldObject,
    ...dimension16PartialFieldObject,
  } as ProductFieldObject<T>;
};

export const getFieldObjectList = (
  pageType: PageType | null,
  entityName?: string,
  listSuffix?: ` - ${string}`,
) => {
  if (!pageType) {
    return '';
  }

  const formattedEntityName =
    entityName && pageType === PageType.Partner ? entityName.toLowerCase() : entityName || '';

  return `${pageType} - ${formattedEntityName}${listSuffix || ''}`;
};

export const getImpressionFieldObject = <T extends PageType>(
  product: Product | undefined,
  items: Record<number, Product[]>,
  productsReturnedCount: number,
  context: PageTrackingContext,
  {
    listSuffix,
  }: {
    listSuffix?: ` - ${string}`;
  } = {},
): ImpressionFieldObject<T> => {
  if (!product) {
    logger.warn('Product is required for getImpressionFieldObject');
    return {} as ImpressionFieldObject<T>;
  }

  const impressionFieldObject = {
    ...generateFieldObject(product, items, productsReturnedCount),
    list: getFieldObjectList(context.pageType, context.entityName, listSuffix),
  };

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    product,
    context,
  ) as ImpressionFieldObject<T>;

  return {
    ...impressionFieldObject,
    ...dimension16PartialFieldObject,
  } as ImpressionFieldObject<T>;
};
